import { useEffect, useRef } from 'react';
import { IconButton } from '../../ui/controls';
import { KeyCaps } from '../../ui/KeyCaps';
import { ALL_TOOLS } from './Toolbar';

export interface ShortcutRow {
  readonly label: string;
  readonly keys: string;
}

export interface ShortcutGroup {
  readonly title: string;
  readonly rows: readonly ShortcutRow[];
}

/**
 * Every key the editor answers to, by what it is for. Each row is a key the editor really
 * binds (EditorPage's tool keys, useEditorCanvas's edit keys); a new binding gets a row here.
 * The tools come from the toolbar's own list, so a tool's key is said in one place.
 */
export function shortcutGroups({ text, comments }: { text: boolean; comments: boolean }): ShortcutGroup[] {
  const tools: ShortcutRow[] = ALL_TOOLS.filter((tool) => tool.shortcut && (text || tool.id !== 'text')).map((tool) => ({ label: tool.label, keys: tool.shortcut! }));
  tools.splice(4, 0, { label: 'Ellipse', keys: 'L' }, { label: 'Line', keys: '\\' });
  if (comments) tools.push({ label: 'Comment', keys: 'C' });
  return [
    { title: 'Tools', rows: tools },
    {
      title: 'Edit',
      rows: [
        { label: 'Undo', keys: '⌘Z' },
        { label: 'Redo', keys: '⇧⌘Z' },
        { label: 'Cut', keys: '⌘X' },
        { label: 'Copy', keys: '⌘C' },
        { label: 'Paste', keys: '⌘V' },
        { label: 'Copy properties', keys: '⌥⌘C' },
        { label: 'Paste properties', keys: '⌥⌘V' },
        { label: 'Duplicate', keys: '⌘D' },
        { label: 'Select all', keys: '⌘A' },
        { label: 'Group', keys: '⌘G' },
        { label: 'Ungroup', keys: '⇧⌘G' },
        { label: 'Pick colour from screen', keys: 'I' },
        { label: 'Pick border colour from screen', keys: '⇧I' },
        { label: 'Delete', keys: '⌫' },
      ],
    },
    {
      title: 'Arrange',
      rows: [
        { label: 'Bring to front', keys: '⇧⌘]' },
        { label: 'Bring forward', keys: '⌘]' },
        { label: 'Send backward', keys: '⌘[' },
        { label: 'Send to back', keys: '⇧⌘[' },
      ],
    },
    {
      title: 'View',
      rows: [
        { label: 'Zoom to fit', keys: '0' },
        { label: 'Actual size', keys: '1' },
        { label: 'Full screen', keys: 'F' },
        { label: 'Layers', keys: 'F7' },
        { label: 'Properties', keys: '⌥⌘I' },
        { label: 'Assets', keys: '⌃⌘S' },
        { label: 'Rulers', keys: '⌥R' },
        { label: 'Grid', keys: "'" },
        { label: 'Snap to grid', keys: '"' },
        { label: 'Guides', keys: ';' },
        { label: 'Lock guides', keys: ':' },
        { label: 'Smart guides', keys: 'U' },
      ],
    },
    {
      title: 'File',
      rows: [
        { label: 'Share', keys: '⇧⌘S' },
        { label: 'Export', keys: '⇧⌘E' },
        { label: 'Keyboard shortcuts', keys: '?' },
      ],
    },
  ];
}

/**
 * The keyboard shortcuts sheet (W5-AR3, Farah's pick B): a centred overlay in two columns,
 * opened with ? or View ▸ Keyboard shortcuts, put away with esc, ? again, the close button or
 * a press outside. It owns the keyboard while it is up, so no key reaches the canvas behind.
 */
export function ShortcutsSheet({ groups, onClose }: { groups: readonly ShortcutGroup[]; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Tab') return;
      event.stopImmediatePropagation();
      if (event.key === 'Escape' || event.key === '?') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // Tools and View are the long lists, so each heads a column and the short groups follow.
  const left = groups.filter((group) => group.title === 'Tools' || group.title === 'Arrange');
  const right = groups.filter((group) => !left.includes(group));
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/20 px-[16px]" onPointerDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcuts-title"
        className="glass-thick ui-pop flex max-h-[min(640px,calc(100vh-48px))] w-full max-w-[560px] flex-col rounded-[18px] text-label"
      >
        <div className="flex h-[48px] shrink-0 items-center gap-[8px] pl-[18px] pr-[11px]">
          <h2 id="shortcuts-title" className="flex-1 text-[15px] font-semibold">
            Keyboard shortcuts
          </h2>
          <IconButton ref={closeRef} icon="close" label="Close" onClick={onClose} />
        </div>
        <div className="ui-scroll grid min-h-0 grid-cols-1 gap-x-[28px] overflow-y-auto px-[18px] pb-[16px] sm:grid-cols-2">
          {[left, right].map((column, index) => (
            <div key={index} className="min-w-0">
              {column.map((group) => (
                <section key={group.title} aria-label={group.title}>
                  <h3 className="pb-[4px] pt-[10px] text-[11px] font-semibold leading-[14px] text-label-2">{group.title}</h3>
                  {group.rows.map((row) => (
                    <div key={row.label} className="flex h-[28px] items-center gap-[8px] text-[13px]">
                      <span className="min-w-0 flex-1 truncate">{row.label}</span>
                      <KeyCaps shortcut={row.keys} />
                    </div>
                  ))}
                </section>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
